import React, { useEffect, useRef } from 'react';
import { formatDollars, formatDollarsWithCents } from '../../shared/givingHelpers';
import DonationFeeSummary from './DonationFeeSummary';
import CustomFieldValueFields from '../../CustomFieldValues/components/CustomFieldValueFields';

const DonationFormBody = ({
  formState,
  buttonText,
  showDedication,
  showOtherAmount = true,
  frequencyLabels,
  onGive,
}) => {
  const {
    amounts,
    amountDescriptions,
    hasDescriptions,
    frequencies,
    parsedDedicationOptions,
    frequency,
    setFrequency,
    selectedAmount,
    isCustom,
    customAmount,
    setCustomAmount,
    dedicateGift,
    setDedicateGift,
    dedicationType,
    setDedicationType,
    honoree,
    setHonoree,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    coverProcessing,
    setCoverProcessing,
    activeAmount,
    processingFee,
    chargeTotal,
    submitting,
    error,
    giftAnswers = [],
    setGiftAnswer,
    fieldErrors = {},
    validationAttempt = 0,
    selectPreset,
    selectCustom,
  } = formState;

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const giftAnswersRef = useRef(null);

  // On each failed Give click, focus the first field with a problem. Keyed on the
  // attempt counter (not fieldErrors) so focus isn't stolen while the donor types.
  useEffect(() => {
    if (validationAttempt === 0) return;
    const refs = { firstName: firstNameRef, lastName: lastNameRef, email: emailRef };
    const firstInvalid = ['firstName', 'lastName', 'email'].find((f) => fieldErrors[f]);
    if (firstInvalid) refs[firstInvalid].current?.focus();
    else if (fieldErrors.giftAnswers) giftAnswersRef.current?.querySelector('input, textarea, select')?.focus();
  }, [validationAttempt]);

  const inputClass = (field) =>
    `gc-dedicate-input${fieldErrors[field] ? ' gc-dedicate-input--error' : ''}`;

  return (
    <>
      {frequencies.length > 1 && (
        <div className="gc-freq-tabs">
          {frequencies.map((freq) => (
            <button
              key={freq}
              className={`gc-freq-tab ${frequency === freq ? 'gc-freq-tab--active' : ''}`}
              onClick={() => setFrequency(freq)}
            >
              {frequencyLabels[freq] || freq}
            </button>
          ))}
        </div>
      )}

      <div className={`gc-amount-grid${hasDescriptions ? ' gc-amount-grid--has-desc' : ''}`}>
        {amounts.map((amount) => {
          const desc = amountDescriptions[amount];
          return (
            <button
              key={amount}
              className={`gc-amount-btn${desc ? ' gc-amount-btn--with-desc' : ''}${!isCustom && selectedAmount === amount ? ' gc-amount-btn--selected' : ''}`}
              onClick={() => selectPreset(amount)}
            >
              <span className="gc-amount-btn-value">{formatDollars(amount)}</span>
              {desc && <span className="gc-amount-btn-desc">{desc}</span>}
            </button>
          );
        })}
        {showOtherAmount && (
          <button
            className={`gc-amount-btn${isCustom ? ' gc-amount-btn--selected' : ''}`}
            onClick={selectCustom}
          >
            <span className="gc-amount-btn-value">Other</span>
          </button>
        )}
      </div>

      {fieldErrors.amount && (
        <div className="gc-field-error gc-field-error--amount" role="alert">{fieldErrors.amount}</div>
      )}

      {isCustom && (
        <div className="gc-custom-amount">
          <span>$</span>
          <input
            type="number"
            placeholder="Enter amount"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            min="1"
            step="0.01"
            autoFocus
          />
        </div>
      )}

      {showDedication && (
        <>
          <div
            className="gc-checkbox-row"
            role="checkbox"
            aria-checked={dedicateGift}
            tabIndex={0}
            onClick={() => setDedicateGift(!dedicateGift)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDedicateGift(!dedicateGift);
              }
            }}
          >
            <div className={`gc-checkbox${dedicateGift ? ' gc-checkbox--checked' : ''}`}>
              {dedicateGift && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span className="gc-checkbox-label-text">Dedicate this gift</span>
          </div>

          {dedicateGift && (
            <div className="gc-dedicate-fields">
              <select
                className="gc-dedicate-select"
                value={dedicationType}
                onChange={(e) => setDedicationType(e.target.value)}
              >
                <option value="">What type of dedication?</option>
                {parsedDedicationOptions.map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </select>
              <input
                type="text"
                className="gc-dedicate-input"
                placeholder="Who do you want to recognize?"
                value={honoree}
                onChange={(e) => setHonoree(e.target.value)}
              />
            </div>
          )}
        </>
      )}

      <div className="gc-donor-fields gc-dedicate-fields">
        <div className="gc-donor-name-row">
          <div className="gc-field">
            <input
              ref={firstNameRef}
              type="text"
              className={inputClass('firstName')}
              placeholder="First name *"
              aria-label="First name (required)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {fieldErrors.firstName && (
              <span className="gc-field-error" role="alert">{fieldErrors.firstName}</span>
            )}
          </div>
          <div className="gc-field">
            <input
              ref={lastNameRef}
              type="text"
              className={inputClass('lastName')}
              placeholder="Last name *"
              aria-label="Last name (required)"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            {fieldErrors.lastName && (
              <span className="gc-field-error" role="alert">{fieldErrors.lastName}</span>
            )}
          </div>
        </div>
        <div className="gc-field">
          <input
            ref={emailRef}
            type="email"
            className={inputClass('email')}
            placeholder="Email address *"
            aria-label="Email address (required)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {fieldErrors.email && (
            <span className="gc-field-error" role="alert">{fieldErrors.email}</span>
          )}
        </div>
      </div>

      {giftAnswers.length > 0 && (
        <div className="gc-donor-fields" ref={giftAnswersRef}>
          <CustomFieldValueFields
            customFieldValues={giftAnswers}
            customFieldValueChanged={(answer) => setGiftAnswer(answer.custom_field_id, answer.value, answer)}
          />
          {fieldErrors.giftAnswers && (
            <span className="gc-field-error" role="alert">{fieldErrors.giftAnswers}</span>
          )}
        </div>
      )}

      {activeAmount > 0 && (
        <DonationFeeSummary
          amount={activeAmount}
          processingFee={processingFee}
          coverProcessing={coverProcessing}
          setCoverProcessing={setCoverProcessing}
        />
      )}

      {error && <div className="gc-form-error" role="alert">{error}</div>}

      <button
        className="gc-give-btn"
        onClick={onGive}
        disabled={submitting}
      >
        {submitting
          ? 'Processing…'
          : activeAmount > 0
            ? `${buttonText || 'Give'} ${formatDollarsWithCents(chargeTotal)}`
            : buttonText || 'Give'}
      </button>
    </>
  );
};

export default DonationFormBody;
